import { TextStyle, TextDecoration } from '../../../../../@types/forms/enums/ElementProperties';

export type ElementTextStyleProps = {
  bold: boolean;
  italic: boolean;
};

export type ElementTextDecorationProps = {
  underline: boolean;
};

/**
 * Converts boolean text style flags to TextStyle enum
 */
export const getTextStyle = (textStyle: ElementTextStyleProps | null): TextStyle => {
  if (!textStyle) {
    return TextStyle.Regular;
  }

  const { bold, italic } = textStyle;

  if (bold && italic) {
    return TextStyle.BoldItalic;
  }

  if (bold) {
    return TextStyle.Bold;
  }

  if (italic) {
    return TextStyle.Italic;
  }

  return TextStyle.Regular;
};

/**
 * Converts TextStyle enum to boolean flags
 */
export const getTextStyleFlags = (textStyle: TextStyle): ElementTextStyleProps => {
  switch (textStyle) {
    case TextStyle.Bold:
      return { bold: true, italic: false };
    case TextStyle.Italic:
      return { bold: false, italic: true };
    case TextStyle.BoldItalic:
      return { bold: true, italic: true };
    case TextStyle.Regular:
    default:
      return { bold: false, italic: false };
  }
};

/**
 * Converts boolean underline flag to TextDecoration enum
 */
export const getTextDecoration = (
  textDecoration: ElementTextDecorationProps | null
): TextDecoration => {
  if (textDecoration?.underline) {
    return TextDecoration.Underline;
  }
  return TextDecoration.None;
};

/**
 * Converts TextDecoration enum to boolean flag
 */
export const getTextDecorationFlag = (textDecoration: TextDecoration): ElementTextDecorationProps => ({
    underline: textDecoration === TextDecoration.Underline,
  });

/**
 * Validates and clamps text rotation value
 */
export const clampTextRotation = (value: number | string): number => {
  const numValue = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(numValue)) {
    return 0;
  }

  if (numValue < 0) {
    return 0;
  }

  if (numValue > 360) {
    return 360;
  }

  return numValue;
};

/**
 * Gets image dimensions from file
 */
export const getImageDimensions = async (
  file: string | ArrayBuffer
): Promise<{ width: number; height: number }> => new Promise((resolve, reject) => {
    const img = new Image();
    img.src = file as string;

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = (error) => {
      reject(error);
    };
  });

/**
 * Converts file to base64
 */
export const getBase64FromFile = (file: File): Promise<string | ArrayBuffer | null> => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });

/**
 * Debounce function for performance
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) & { cancel: () => void; flush: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;

  const debouncedFunc = (...args: Parameters<T>) => {
    lastArgs = args;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      lastArgs = null;
    }, delay);
  };

  debouncedFunc.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
  };

  debouncedFunc.flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (lastArgs) {
      func(...lastArgs);
      lastArgs = null;
    }
  };

  return debouncedFunc;
};
